import { useEffect, useState } from 'react'
import { Page, TopBar } from '../components/Shell'
import { SignSheet } from '../components/SignSheet'
import { Skeleton } from '../components/Skeleton'
import { useStore } from '../state/store'
import { useToast } from '../components/Toast'
import { readDay } from '../state/day'
import { clockIn, clockOut, loadOnClock, loadTimeCards, type Punch } from '../lib/api'
import type { Employee } from '../lib/types'
import { Working } from '../components/StarTick'

const TZ = 'America/New_York'
const tm = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: TZ })
const dayLabel = (d: string) => new Date(d + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })
const hrs = (h: number) => { const m = Math.round(h * 60); return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m` }
const ny = (d = new Date()) => d.toLocaleDateString('en-CA', { timeZone: TZ })
/** Monday of the week containing the NY date `d`, as YYYY-MM-DD. */
function monday(d: string) { const x = new Date(d + 'T12:00:00Z'); const dow = (x.getUTCDay() + 6) % 7; x.setUTCDate(x.getUTCDate() - dow); return x.toISOString().slice(0, 10) }
function addDays(d: string, n: number) { const x = new Date(d + 'T12:00:00Z'); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10) }

/** Time clock: who is on the clock, your time card, clock out. Owners see everyone's week and can clock someone out. */
export function TimeClock({ onLock }: { onLock: () => void }) {
  const { employees } = useStore()
  const toast = useToast()
  const me = readDay()
  const meE = employees.find((e) => e.employee_id === me?.employee_id)
  const owner = meE?.rights === 'owner'
  const [onClock, setOnClock] = useState<Punch[] | null>(null)
  const [cards, setCards] = useState<Punch[] | null>(null)
  const [week, setWeek] = useState(() => monday(ny()))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [outFor, setOutFor] = useState<Punch | null>(null)
  const [, tick] = useState(0)

  const load = () => {
    loadOnClock().then(setOnClock).catch((e) => setErr((e as Error).message))
    loadTimeCards(addDays(week, -7), owner ? undefined : me?.employee_id).then(setCards).catch((e) => setErr((e as Error).message))
  }
  useEffect(load, [week, owner])  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const t = window.setInterval(() => tick((n) => n + 1), 30_000); return () => window.clearInterval(t) }, [])

  const mine = onClock?.find((p) => p.employee_id === me?.employee_id) ?? null
  const live = (p: Punch) => p.clock_out ? p.hours : (Date.now() - new Date(p.clock_in).getTime()) / 3600_000
  const weekEnd = addDays(week, 7)
  const inWeek = (p: Punch, from: string, to: string) => p.day >= from && p.day < to
  const myWeek = (cards ?? []).filter((p) => p.employee_id === me?.employee_id && inWeek(p, week, weekEnd))
  const myLast = (cards ?? []).filter((p) => p.employee_id === me?.employee_id && inWeek(p, addDays(week, -7), week))
  const sum = (ps: Punch[]) => ps.reduce((a, p) => a + live(p), 0)

  const doOut = async () => {
    if (!me) return
    setBusy(true); setErr('')
    try { await clockOut(me.employee_id); onLock() }
    catch (ex) { setErr((ex as Error).message); setBusy(false) }
  }
  const doIn = async () => {
    if (!me) return
    setBusy(true); setErr('')
    try { await clockIn(me.employee_id); load() } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }
  const doOutFor = async (o: Employee) => {
    if (!outFor) return
    setBusy(true); setErr('')
    try { await clockOut(o.employee_id, outFor.employee_id); toast(`${outFor.name} clocked out by ${o.name}`); setOutFor(null); load() }
    catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }

  const byPerson = new Map<string, { name: string; ps: Punch[] }>()
  for (const p of (cards ?? []).filter((x) => inWeek(x, week, weekEnd))) {
    const cur = byPerson.get(p.employee_id) ?? { name: p.name, ps: [] }; cur.ps.push(p); byPerson.set(p.employee_id, cur)
  }
  const thisWeek = week === monday(ny())

  return (
    <Page bar={<TopBar back="Home" backTo="/" title="Time clock" caption={new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: TZ })} />}
      aside={<>
        <div className="aside__body">
          <div className="icard-head"><b>{me?.name ?? ''}</b><span>{mine ? `On the clock since ${tm(mine.clock_in)}` : 'Not on the clock'}</span></div>
          <div className="card" style={{ margin: '0 20px' }}>
            <div className="row"><span className="row__label">Today</span><span className="row__value set">{mine ? hrs(live(mine)) : '—'}</span></div>
            <div className="row"><span className="row__label">{thisWeek ? 'This week' : 'That week'}</span><span className="row__value set">{cards ? hrs(sum(myWeek)) : '…'}</span></div>
            <div className="row"><span className="row__label">Week before</span><span className="row__value set">{cards ? hrs(sum(myLast)) : '…'}</span></div>
          </div>
          <div className="section-title" style={{ padding: '14px 24px 6px' }}>Your shifts</div>
          <div className="card" style={{ margin: '0 20px' }}>
            {cards === null ? <Skeleton rows={3} card={false} /> : myWeek.length === 0 ? <div className="empty">No shifts {thisWeek ? 'this week' : 'that week'}.</div> :
              myWeek.map((p) => (
                <div key={p.id} className="tc-row">
                  <span><span className="tc-row__name">{dayLabel(p.day)}</span><span className="tc-row__sub" style={{ display: 'block' }}>{tm(p.clock_in)} – {p.clock_out ? tm(p.clock_out) : 'now'}{p.out_reason === 'missed clock-out' ? '' : p.out_reason ? ` · ${p.out_reason}` : ''}</span></span>
                  <span>{p.out_reason === 'missed clock-out' && <span className="tc-flag">Missed clock-out</span>}</span>
                  <span className="tc-row__h">{hrs(live(p))}</span>
                </div>
              ))}
          </div>
          {err && <div className="banner" style={{ margin: '12px 20px 0', background: '#d70015' }}>{err}</div>}
        </div>
        <div className="aside__foot">
          {mine
            ? <button type="button" className="cta cta--center" disabled={busy} onClick={doOut}>{busy ? <Working>One moment</Working> : 'Clock out and lock'}</button>
            : <button type="button" className="cta cta--center" disabled={busy || !me} onClick={doIn}>{busy ? <Working>One moment</Working> : 'Clock in'}</button>}
        </div>
      </>}>
      <div className="section-title">On the clock now</div>
      <div className="card">
        {onClock === null ? <Skeleton rows={2} card={false} /> : onClock.length === 0 ? <div className="empty">Nobody is on the clock.</div> :
          onClock.map((p) => (
            <div key={p.id} className="tc-row">
              <span><span className="tc-row__name">{p.name}</span><span className="tc-row__sub" style={{ display: 'block' }}>In at {tm(p.clock_in)} · ID {p.employee_id}</span></span>
              {p.employee_id !== me?.employee_id
                ? <button type="button" className="btn-secondary" onClick={() => setOutFor(p)}>Clock out (owner)</button>
                : <span className="tc-row__sub">You</span>}
              <span className="tc-row__h">{hrs(live(p))}</span>
            </div>
          ))}
      </div>
      <div className="hint">Clock in when you get to the showroom and out when you leave. The last person out closes out on the Close out tab, which clocks them out.</div>

      {owner && <>
        <div className="section-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flexGrow: 1 }}>Everyone, week of {dayLabel(week)}</span>
          <button type="button" className="btn-secondary" onClick={() => setWeek(addDays(week, -7))}>‹ Earlier</button>
          <button type="button" className="btn-secondary" disabled={thisWeek} onClick={() => setWeek(addDays(week, 7))}>Later ›</button>
        </div>
        <div className="card">
          {cards === null ? <Skeleton rows={4} card={false} /> : byPerson.size === 0 ? <div className="empty">No shifts that week.</div> :
            [...byPerson.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name)).map(([id, x]) => (
              <div key={id} className="tc-row" style={{ alignItems: 'start' }}>
                <span><span className="tc-row__name">{x.name}</span>
                  {x.ps.slice().reverse().map((p) => <span key={p.id} className="tc-row__sub" style={{ display: 'block' }}>{dayLabel(p.day)} · {tm(p.clock_in)} – {p.clock_out ? tm(p.clock_out) : 'now'} · {hrs(live(p))}{p.out_reason === 'missed clock-out' ? <span className="tc-flag"> · missed clock-out</span> : p.out_reason === 'by owner' ? ` · out by ${p.out_by}` : ''}</span>)}
                </span>
                <span className="tc-row__sub">{new Set(x.ps.map((p) => p.day)).size} days</span>
                <span className="tc-row__h">{hrs(sum(x.ps))}</span>
              </div>
            ))}
        </div>
        <div className="hint">A shift left open overnight closes itself as a missed clock-out at that day's close time (or with no hours if the day was never closed). Check those against the schedule.</div>
      </>}
      {outFor && <SignSheet title={`Owner ID to clock ${outFor.name} out`} ownerOnly onConfirm={doOutFor} onClose={() => setOutFor(null)} busy={busy} />}
    </Page>
  )
}
